import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (parent: HTMLElement, options: any) => void;
          prompt: (notification?: any) => void;
          cancel: () => void;
        };
        oauth2: {
          initTokenClient: (config: any) => {
            requestAccessToken: (overrideConfig?: any) => void;
          };
        };
      };
    };
  }
}

interface GoogleAdminLoginProps {
  onSuccess?: () => void;
  onError?: (errorMsg: string) => void;
  className?: string;
  autoPrompt?: boolean;
}

export const GoogleAdminLogin: React.FC<GoogleAdminLoginProps> = ({
  onSuccess,
  onError,
  className = '',
  autoPrompt = true,
}) => {
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tokenClient, setTokenClient] = useState<any>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Official Google OAuth Client ID for For The Truth Publishing
  const googleClientId =
    import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    '654189163199-2gvqm197les63h8ml7vldru6s7ie77ld.apps.googleusercontent.com';

  const authenticateWithBackend = async (payload: { credential?: string; accessToken?: string }) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res = await api.post('/auth/google-admin', payload);

      if (res.data?.success) {
        const { token, user, clientProfile } = res.data.data;
        login(token, user, clientProfile);
        onSuccess?.();
        navigate('/admin');
      } else {
        const err = res.data?.error?.message || 'Administrative access denied.';
        setErrorMessage(err);
        onError?.(err);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error?.message ||
        'Access Denied: Your Google account is not authorized for administrative access.';
      setErrorMessage(msg);
      onError?.(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCredentialResponse = async (response: any) => {
    if (response.credential) {
      await authenticateWithBackend({ credential: response.credential });
    } else {
      const err = 'No credential received from Google.';
      setErrorMessage(err);
      onError?.(err);
    }
  };

  const handleDirectOAuthClick = () => {
    if (tokenClient) {
      tokenClient.requestAccessToken({ prompt: 'select_account' });
    } else if (window.google?.accounts?.oauth2) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: 'openid email profile',
        callback: (tokenResponse: any) => {
          if (tokenResponse?.access_token) {
            authenticateWithBackend({ accessToken: tokenResponse.access_token });
          }
        },
      });
      client.requestAccessToken({ prompt: 'select_account' });
    }
  };

  useEffect(() => {
    let checkInterval: any = null;

    const initializeGoogleSignIn = () => {
      if (window.google?.accounts && googleBtnRef.current) {
        try {
          // 1. Initialize Google Identity Services One-Tap & Button
          if (window.google.accounts.id) {
            window.google.accounts.id.initialize({
              client_id: googleClientId,
              callback: handleCredentialResponse,
              auto_select: false,
              cancel_on_tap_outside: true,
              context: 'signin',
              ux_mode: 'popup',
              itp_support: true,
            });

            // Render official Google button
            googleBtnRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline',
              size: 'large',
              type: 'standard',
              shape: 'rectangular',
              text: 'signin_with',
              logo_alignment: 'left',
              width: googleBtnRef.current.offsetWidth || 340,
            });

            if (autoPrompt) {
              window.google.accounts.id.prompt();
            }
          }

          // 2. Initialize OAuth2 Token Client for instant direct popups
          if (window.google.accounts.oauth2) {
            const client = window.google.accounts.oauth2.initTokenClient({
              client_id: googleClientId,
              scope: 'openid email profile',
              callback: (tokenResponse: any) => {
                if (tokenResponse?.access_token) {
                  authenticateWithBackend({ accessToken: tokenResponse.access_token });
                }
              },
            });
            setTokenClient(client);
          }
        } catch (e) {
          console.warn('Google Identity Services initialization notice:', e);
        }
      }
    };

    if (window.google?.accounts) {
      initializeGoogleSignIn();
    } else {
      checkInterval = setInterval(() => {
        if (window.google?.accounts) {
          initializeGoogleSignIn();
          clearInterval(checkInterval);
        }
      }, 300);
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [googleClientId, autoPrompt]);

  return (
    <div className={`w-full space-y-4 ${className}`}>
      {/* Error Alert */}
      {errorMessage && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2.5 animate-fadeIn">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-bold block">Access Restricted</span>
            <span className="text-[11px] leading-relaxed block text-rose-700">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading ? (
        <div className="w-full py-3.5 px-4 rounded-xl bg-slate-900 text-white flex items-center justify-center gap-3 text-xs font-semibold shadow-md">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          <span>Verifying Administrative Credentials with Google...</span>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center space-y-3">
          {/* Official Google Button Container */}
          <div
            ref={googleBtnRef}
            className="w-full flex justify-center min-h-[44px] overflow-hidden rounded-lg"
          >
            {/* Direct 1-Click fallback button */}
            <button
              type="button"
              onClick={handleDirectOAuthClick}
              className="w-full py-2.5 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center justify-center gap-3 shadow-xs transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              <span>Sign in with Google</span>
            </button>
          </div>

          <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-brand-600" />
            <span>Strict OAuth 2.0 Whitelist Security Enforced</span>
          </div>
        </div>
      )}
    </div>
  );
};
